import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/hooks/useAnalytics";
import { Ticket, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Redeem() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (user?.paid && !done) {
      // Already enrolled — direct them to dashboard
      toast.info("You're already enrolled — no code needed.");
      nav("/dashboard", { replace: true });
    }
  }, [user, nav, done]);

  const submit = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { toast.error("Enter a gift code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/gifts/redeem", { code: c });
      setDone(data);
      trackEvent("gift_redeem", { code: c });
      await refresh();
      toast.success("Gift redeemed — course unlocked!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't redeem — check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-2xl mx-auto px-6 sm:px-8 py-14" data-testid="redeem-page">
        <div className="overline mb-3"><span className="red-bar" />Redeem gift</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-3">
          Got a gift code?
        </h1>
        <p className="text-zinc-700 mb-8 max-w-xl leading-relaxed">
          Enter the code from your email to unlock the full 140-hour curriculum on this account.
          Codes look like <code className="font-mono text-zinc-900">APMGIFT-XXXXX-XXXXX</code>.
        </p>

        {done ? (
          <div className="card-tech p-8 border-green-300 bg-green-50/40" data-testid="redeem-success">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
              <div className="font-heading text-2xl font-bold tracking-tight">Redeemed. You're in.</div>
            </div>
            <p className="text-zinc-700 mb-2">
              Gift from <strong>{done.from || "a friend"}</strong> applied. Your account unlocked the full
              NICET + NYC S-98 curriculum.
            </p>
            <p className="font-mono text-xs text-zinc-500 mb-6">Code: {done.code}</p>
            <Link to="/dashboard" data-testid="redeem-dashboard-btn">
              <button className="btn-primary">Go to my dashboard <ArrowRight className="w-4 h-4" /></button>
            </Link>
          </div>
        ) : (
          <div className="card-tech p-8">
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-600 mb-2">
              Gift code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="APMGIFT-XXXXX-XXXXX"
              className="w-full border-2 border-zinc-200 focus:border-red-600 outline-none px-4 py-3 font-mono text-lg tracking-wider"
              autoFocus
              data-testid="redeem-code-input"
            />
            <button
              onClick={submit}
              disabled={loading || !code.trim()}
              className="btn-primary justify-center w-full mt-6 disabled:opacity-50"
              data-testid="redeem-submit-btn"
            >
              <Ticket className="w-4 h-4" />
              {loading ? "Redeeming…" : "Redeem gift"}
            </button>
            <p className="text-xs text-zinc-500 font-mono mt-4 leading-relaxed">
              One code per account. Codes are single-use; once redeemed they cannot be reused or refunded.
            </p>
          </div>
        )}

        <div className="mt-8 text-sm text-zinc-600">
          <Link to="/enroll" className="hover:text-red-600" data-testid="redeem-back-to-enroll">
            ← Don't have a code? Enroll directly
          </Link>
        </div>
      </main>
    </div>
  );
}
